import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import strings from "../../constants/StringConstants";

export type ToastVariant = "success" | "error" | "warning" | "info";

interface CustomProps {
  showNotification: boolean;
  verticalPosition?: "top" | "bottom";
  horizontalPosition?: "left" | "right" | "center";
  variant: string;
  title: string;
  message: string;
  icon?: React.ReactNode;
  handleClose: () => void;
}

const VARIANT_STYLES: Record<
  string,
  { bg: string; color: string; iconBg: string; border: string }
> = {
  [strings.success]: {
    bg: "#E8F5E9",
    color: "#1B5E20",
    iconBg: "#2E7D32",
    border: "#C8E6C9",
  },
  [strings.error]: {
    bg: "#FFEBEE",
    color: "#B71C1C",
    iconBg: "#C62828",
    border: "#FFCDD2",
  },
  [strings.warning]: {
    bg: "#FFF8E1",
    color: "#E65100",
    iconBg: "#EF6C00",
    border: "#FFE0B2",
  },
  [strings.info]: {
    bg: "#E3F2FD",
    color: "#0D47A1",
    iconBg: "#1565C0",
    border: "#BBDEFB",
  },
};

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
    <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ErrorIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
    <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WarningIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
    <path d="M12 9v4M12 17h.01" strokeLinecap="round" />
    <path
      d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InfoIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" strokeLinecap="round" />
  </svg>
);

const CloseIcon = ({ color }: { color: string }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.25">
    <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const defaultIconFor = (variant: string) => {
  switch (variant) {
    case strings.error:
      return <ErrorIcon />;
    case strings.warning:
      return <WarningIcon />;
    case strings.info:
      return <InfoIcon />;
    case strings.success:
    default:
      return <CheckIcon />;
  }
};

const positionClass = (
  vertical: CustomProps["verticalPosition"] = "bottom",
  horizontal: CustomProps["horizontalPosition"] = "center"
) => {
  const v = vertical === "top" ? "top-5 sm:top-7" : "bottom-5 sm:bottom-7";
  const h =
    horizontal === "left"
      ? "left-4 sm:left-6"
      : horizontal === "right"
        ? "right-4 sm:right-6"
        : "left-1/2 -translate-x-1/2";
  return `${v} ${h}`;
};

const CustomNotification = (props: CustomProps) => {
  const theme = VARIANT_STYLES[props.variant] || VARIANT_STYLES[strings.success];
  const displayText = props.message || props.title || "";

  useEffect(() => {
    if (!props.showNotification) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [props.showNotification, props.handleClose]);

  if (!props.showNotification || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      className={`pointer-events-none fixed z-[10000] flex w-[min(720px,calc(100vw-32px))] justify-center ${positionClass(
        props.verticalPosition,
        props.horizontalPosition
      )}`}
      role="presentation"
    >
      <div
        role="alert"
        className="pointer-events-auto flex w-full items-center gap-3 rounded-lg border px-5 py-3 shadow-[0_4px_18px_rgba(15,23,42,0.12)] animate-[yhToastIn_180ms_ease-out]"
        style={{
          backgroundColor: theme.bg,
          color: theme.color,
          borderColor: theme.border,
        }}
      >
        <span
          className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: theme.iconBg }}
        >
          {props.icon || defaultIconFor(props.variant)}
        </span>

        <p className="min-w-0 flex-1 text-center text-[13px] font-semibold leading-snug break-words">
          {displayText}
        </p>

        <button
          type="button"
          aria-label="Close"
          onClick={props.handleClose}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md opacity-85 transition hover:bg-black/5 hover:opacity-100"
          style={{ color: theme.color }}
        >
          <CloseIcon color={theme.color} />
        </button>
      </div>

      <style>{`
        @keyframes yhToastIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default CustomNotification;
